import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveApiOfficeCode } from '../../utils/adminCompany';


const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    totalPages: null,
    totalRecords: null,
    isLoading: false,
    error: null,
}

// Define an async thunk for fetching paginated data
export const fetchPaginatedDueDateExtensionNormal = createAsyncThunk(
    'generalServices/fetchDueDateExtensionNormal',
    async ({ page, pageSize, dateFrom, dateTo, applicationProgressId, objectionStatus, officeCode }, { getState }) => {
        const { auth } = getState();
        const { token, code, hierarchy, showAllApplicationsFlag, roleId, selectedAdminCompanyCode } = auth;
        const effectiveOfficeCode = resolveApiOfficeCode(code, roleId, officeCode, selectedAdminCompanyCode);

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const formdata = new FormData();
        formdata.append("pageNumber", page);
        formdata.append("pageSize", pageSize);
        formdata.append("officeCode", effectiveOfficeCode);
        formdata.append("officeLevel", hierarchy);
        formdata.append("showAllApplicationFlag", showAllApplicationsFlag);
        formdata.append("overDueFlag", 0);
        formdata.append("dateFrom", dateFrom);
        formdata.append("dateTo", dateTo);
        formdata.append("applicationProgressId", applicationProgressId);
        formdata.append("objectionStatus", objectionStatus);

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: formdata,
            redirect: "follow"
        };

        const response = await fetch(cfpBaseUrl + `comman/getAllDueDateExtensionApplications`, requestOptions);
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Stats API issue!');
        }

        const result = await response.json();

        if (result.status !== "200" && result.status !== "400") {
            throw new Error(result.message, 'printing error message' || 'Unable to fetch stats!');
        }

        return result;
    }
);

const dueDateExtensionPaginatedNormalSlice = createSlice({
    name: 'dueDateExtensionNormalPaginated',
    initialState: initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = ''
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload
        },
        resetObjectionStatusFilter(state) {
            state.applicationProgressId = ''
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload
        },
        resetDateFilter(state) {
            state.dateFrom = ''
            state.dateTo = ''
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        resetState(state) {
            state.data = []
            state.currentPage = 1
            state.pageSize = 20
            state.dateFrom = ''
            state.dateTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.totalPages = null
            state.totalRecords = null
            state.isLoading = false
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedDueDateExtensionNormal.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaginatedDueDateExtensionNormal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;

                const payload = action.payload || {};
                const rows = Array.isArray(payload.data) ? payload.data : [];
                state.data = rows;

                let totalRecords = Number(
                    payload.totalRecords ?? payload.totalCount ?? payload.TotalRecords
                );
                let totalPages = Number(
                    payload.totalPages ?? payload.TotalPages
                );

                if (!Number.isFinite(totalRecords) || totalRecords < 0) {
                    totalRecords = 0;
                }
                if (!Number.isFinite(totalPages) || totalPages < 0) {
                    totalPages = 0;
                }

                if (totalRecords === 0 && rows.length > 0) {
                    const previous = Number(state.totalRecords);
                    totalRecords = Number.isFinite(previous) && previous > rows.length
                        ? previous
                        : rows.length;
                }

                if (totalPages === 0 && totalRecords > 0) {
                    totalPages = Math.max(
                        1,
                        Math.ceil(totalRecords / (state.pageSize || 20))
                    );
                }

                if (totalRecords === 0 && totalPages > 1) {
                    totalRecords = totalPages * (state.pageSize || 20);
                }

                state.totalRecords = totalRecords;
                state.totalPages = totalPages || 1;
            })
            .addCase(fetchPaginatedDueDateExtensionNormal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            });
    },
});

export const { nextPage, previousPage, firstPage, lastPage, filterProgressId, filterObjectionStatus, filterDateFrom, filterDateTo, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetState } = dueDateExtensionPaginatedNormalSlice.actions;

export default dueDateExtensionPaginatedNormalSlice.reducer;
